/**
 * ShopsScreen — "Find shops" (route `/shops`, design revamp).
 *
 * Production translation of `Shops` in
 * `docs/GardEn/handoff-2026-06/garden/screens-shops.jsx`:
 *   - a header (title + search `Field` + a horizontal `Pill` category row),
 *   - a map (`MapPlaceholder`) + a seller list (`SellerCard`),
 *   - a seller detail `BottomSheet` (`SellerDetail`) opened by a card tap or
 *     a map pin.
 *
 * Layout responds to the `md` (≥768px) breakpoint via the JS `isDesktop`
 * boolean (`useIsDesktop`, a `matchMedia` mirror) rather than CSS hide/show:
 * the map + list mount EXACTLY ONCE (no duplicate nodes/queries), mirroring
 * the prototype's `desktop ? … : …`. Mobile stacks the map over the list;
 * desktop is a 2-column grid (map left, list right). The `BottomSheet` slide
 * direction + map height read the same boolean. The header still uses `md:`
 * utilities for its max-width.
 *
 * Data: `useSellers({ category, q })` drives both columns; `q` is debounced
 * (`useDebouncedValue`). The category Pills map 1:1 to the prototype's
 * `["all", tile, paint, electrics, plumbing, rough]` buckets — index 0 ("All")
 * sends no `category`. States: loading (Skeleton list + map), empty
 * ("nothing matched"), and error (retry). Tokens via `--g-*`; every string is
 * a `shops` i18n key (RU/KY/EN, ADR-028).
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useMatch, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import {
  BottomSheet,
  Button,
  Field,
  Icon,
  Pill,
  Skeleton,
} from "@garden/ui";
import { Stagger, StaggerItem } from "@/components/motion";
import type { SupportedLanguage } from "@/lib/i18n";
import { useUiStore } from "@/lib/ui-store";
import { listSellers, type SellerSummary } from "@/lib/garden-api";

import {
  registerShopsI18n,
  SELLERS_STALE_TIME_MS,
  useSellers,
} from "@/features/shops/api";
import { SellerDetail } from "@/features/shops/SellerDetail";
import { SellerRow } from "@/features/shops/SellerRow";
import { ShopsMapPanel } from "@/features/shops/ShopsMapPanel";
import { buildLocatorPoints } from "@/features/shops/SellerLocatorPlan";
import { useDebouncedValue } from "@/features/shops/useDebouncedValue";
import { useIsDesktop } from "@/features/shops/useIsDesktop";

/**
 * The category buckets the Pill row exposes, aligned to the prototype's
 * `catMap`. Index 0 ("all") sends no `category` to the finder. The labels
 * come from the `shops.cats` copy array (same order: Все/Плитка/Краски/
 * Электрика/Сантехника/Черновые).
 *
 * SCRUM-141: these must be real category slugs from `CATEGORY_TREE`
 * (`apps/api/garden/fixtures/seed_local.py`) — the seller finder resolves
 * `?category=` against the live tree and matches NO seller on an unknown
 * slug (fail-closed by design, not fail-open). The former "tile" /
 * "electrics" / "rough" values matched nothing (the tree has no root with
 * those slugs), so those three tabs always returned zero sellers. "paint"
 * and "plumbing" were already correct root slugs — that's why only the
 * other three appeared broken. "Плитка" (Tile) maps to the "flooring" root
 * (its leaves include flooring-tile); "Черновые" (Rough-in) maps to "walls"
 * (its leaves — drywall, putty — are the rough-stage wall-prep materials;
 * the tree has no dedicated "rough-in" root).
 */
export const SHOP_CATEGORIES = [
  "all",
  "flooring",
  "paint",
  "electric",
  "plumbing",
  "walls",
] as const;

const MOBILE_MAP_HEIGHT = 200;

export function ShopsScreen(): ReactElement {
  registerShopsI18n();
  const { t } = useTranslation("shops");
  const lang = useUiStore((s) => s.language) as SupportedLanguage;
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  // SCRUM-140: optional deep-link param — `/shops/sellers/:sellerId` opens
  // the detail sheet for that seller directly (e.g. from a chat seller-match
  // card), instead of dead-ending on the plain finder. `/shops` is now a
  // single splat route (App.tsx) rather than two sibling <Route>s — this
  // component is mounted once for both "/shops" and "/shops/sellers/:id",
  // so sellerId is read via useMatch against the sub-path instead of a
  // dedicated route param (SCRUM-147 follow-up — avoids remounting
  // ShopsMap, which was resetting the 2GIS map's zoom on every seller click).
  const sellerMatch = useMatch("/shops/sellers/:sellerId");
  const sellerId = sellerMatch?.params.sellerId;

  const [categoryIndex, setCategoryIndex] = useState(0);
  const [query, setQuery] = useState("");
  const [openSeller, setOpenSeller] = useState<SellerSummary | null>(null);
  /** Row ⇄ pin hover link — the reason map and ledger sit side by side. */
  const [hoveredSellerId, setHoveredSellerId] = useState<string | null>(null);
  /**
   * Change-persistence (RAISE, donor: gate board). The row the user last
   * opened keeps its mark after the sheet closes, so returning to a scrolled
   * ledger still shows where they were. Cleared when another row is opened.
   */
  const [markedSellerId, setMarkedSellerId] = useState<string | null>(null);
  /** Bumped by the map panel's retry so the SDK genuinely re-attempts. */
  const [mapAttempt, setMapAttempt] = useState(0);
  const [mapFailed, setMapFailed] = useState(false);

  const openSellerAndSync = (seller: SellerSummary): void => {
    setOpenSeller(seller);
    setMarkedSellerId(seller.id);
    navigate(`/shops/sellers/${seller.id}`, { replace: true });
  };
  const closeSellerAndSync = (): void => {
    setOpenSeller(null);
    if (sellerId !== undefined) navigate("/shops", { replace: true });
  };

  const debouncedQuery = useDebouncedValue(query);
  const category =
    categoryIndex === 0 ? undefined : SHOP_CATEGORIES[categoryIndex];
  const trimmedQuery = debouncedQuery.trim();

  const sellersQuery = useSellers({
    ...(category ? { category } : {}),
    ...(trimmedQuery ? { q: trimmedQuery } : {}),
  });

  // Memoise on the query data (a stable reference per fetch) so neither the
  // `sellers` array nor the derived pins churn a new reference every render.
  const sellers = useMemo<SellerSummary[]>(
    () => sellersQuery.data?.sellers ?? [],
    [sellersQuery.data],
  );
  /**
   * The locator plan's points. Only sellers that genuinely carry coordinates
   * are plotted — `map-projection` will happily hash a coordinate-less seller
   * onto a stable but INVENTED grid cell, and on a surface a buyer navigates
   * by, an invented position is a lie. The layout reports how many it had to
   * leave off so the plan can say so out loud.
   */
  const locatorLayout = useMemo(() => buildLocatorPoints(sellers), [sellers]);

  // SCRUM-140 deep-link resolution: the seller named by `sellerId` may not be
  // in the currently-filtered `sellers` list (e.g. category/search filters
  // active, or the sheet was opened fresh from a chat link). Check the loaded
  // list first; only hit the network for the full unfiltered directory when
  // that misses, and only while a `sellerId` param is actually present.
  const foundInList = sellerId
    ? (sellers.find((s) => s.id === sellerId) ?? null)
    : null;
  const directoryQuery = useQuery({
    queryKey: ["shops", "seller-directory-lookup"],
    queryFn: () => listSellers({}),
    staleTime: SELLERS_STALE_TIME_MS,
    // Wait for the main list to settle before deciding the seller is
    // missing — otherwise every deep-link visit fires a redundant fetch
    // during the brief window where `sellers` is still `[]`.
    enabled:
      sellerId !== undefined && !sellersQuery.isPending && foundInList === null,
  });
  const foundInDirectory = sellerId
    ? (directoryQuery.data?.sellers.find((s) => s.id === sellerId) ?? null)
    : null;

  useEffect(() => {
    if (sellerId === undefined) return;
    const resolved = foundInList ?? foundInDirectory;
    if (resolved !== null) setOpenSeller(resolved);
  }, [sellerId, foundInList, foundInDirectory]);

  const catLabels = t("cats", { returnObjects: true }) as string[];

  // ── Map column (shared between mobile + desktop) ───────────────────────────
  // Real 2GIS map when a MapGL key is baked in (VITE_2GIS_API_KEY); otherwise
  // the faux MapPlaceholder. 2GIS is the only provider (TRD §5.3).
  const hasMapKey =
    typeof import.meta.env.VITE_2GIS_API_KEY === "string" &&
    import.meta.env.VITE_2GIS_API_KEY.length > 0;
  const mapColumn = (height: number | string): ReactElement =>
    sellersQuery.isPending ? (
      <Skeleton w="100%" h={height} r={14} />
    ) : (
      <ShopsMapPanel
        sellers={sellers}
        layout={locatorLayout}
        live={hasMapKey && !mapFailed}
        attempt={mapAttempt}
        failed={mapFailed}
        hasKey={hasMapKey}
        height={height}
        selectedSeller={openSeller}
        hoveredSellerId={hoveredSellerId}
        onSelect={openSellerAndSync}
        onHover={setHoveredSellerId}
        onMapError={() => setMapFailed(true)}
        onRetry={() => {
          setMapFailed(false);
          setMapAttempt((n) => n + 1);
        }}
        onClearSelection={closeSellerAndSync}
        lang={lang}
      />
    );

  // ── List column (shared) ───────────────────────────────────────────────────
  const listColumn = (): ReactElement => {
    if (sellersQuery.isPending) {
      /* The skeleton mirrors the LEDGER it stands in for — same ruled rows,
         same three columns, same row height — so nothing shifts when the data
         lands. A skeleton shaped like the old cards would have caused exactly
         the layout jump it exists to prevent. */
      return (
        <div data-testid="shops-list-loading">
          <div className="border-b-2 border-[var(--g-rule-strong)] px-3 pb-1.5">
            <Skeleton w={90} h={9} />
          </div>
          <div className="g-ruled">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-3.5">
                <Skeleton w={18} h={12} />
                <div className="flex flex-1 flex-col gap-2">
                  <Skeleton w="58%" h={13} />
                  <Skeleton w="38%" h={10} />
                </div>
                <Skeleton w={44} h={16} />
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (sellersQuery.isError) {
      return (
        <div
          role="alert"
          data-testid="shops-list-error"
          className="flex flex-col items-center gap-3 px-5 py-10 text-center"
        >
          <Icon
            name="warn"
            size={32}
            className="text-[var(--g-ink-3)]"
            aria-hidden
          />
          <div className="text-sm font-semibold text-foreground">
            {t("error")}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void sellersQuery.refetch()}
            data-testid="shops-list-retry"
          >
            {t("retry")}
          </Button>
        </div>
      );
    }

    if (sellers.length === 0) {
      return (
        <div
          data-testid="shops-list-empty"
          className="flex flex-col items-center gap-2.5 px-5 py-10 text-center text-[var(--g-ink-3)]"
        >
          <Icon name="search" size={34} className="opacity-50" aria-hidden />
          <div className="text-sm font-semibold">{t("noResults")}</div>
        </div>
      );
    }

    /*
     * The ledger. A ruled list, not a stack of floating cards: one drawn rule
     * between rows (`.g-ruled`), one column head naming what each column is,
     * and every row on the identical name / stall / stock triplet so a wall of
     * sellers reads as ONE instrument (RAISE: label grid + visible armature).
     */
    return (
      <div data-testid="shops-list">
        <div className="flex items-baseline justify-between gap-3 border-b-2 border-[var(--g-rule-strong)] px-3 pb-1.5">
          <span className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--g-ink-2)]">
            {t("ledger.seller")}
          </span>
          <span className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--g-ink-2)]">
            {t("ledger.stock")}
          </span>
        </div>
        <Stagger as="div" className="g-ruled">
          {sellers.map((seller, i) => (
            <StaggerItem as="div" key={seller.id}>
              <SellerRow
                seller={seller}
                index={i + 1}
                onOpen={openSellerAndSync}
                onHover={setHoveredSellerId}
                selected={openSeller?.id === seller.id}
                highlighted={hoveredSellerId === seller.id}
                marked={markedSellerId === seller.id && openSeller === null}
                lang={lang}
              />
            </StaggerItem>
          ))}
        </Stagger>
        <p className="px-3 pt-3 text-[11.5px] text-[var(--g-ink-3)]">
          {t("sellersCount", { count: sellers.length })}
        </p>
      </div>
    );
  };

  return (
    <div
      data-testid="shops-screen"
      className="flex h-full min-h-full flex-col bg-[var(--g-ground)]"
    >
      {/* Header */}
      <header className="border-b border-[var(--g-rule)] bg-[var(--g-surface)] px-5 py-3.5">
        <div className="mx-auto w-full max-w-[520px] md:max-w-[1080px]">
          {/* The display voice, matching every other screen title in the app
              (Каталог товаров, Настройки, the assistant). It was the one
              consumer h1 still set in the body face. */}
          <h1 className="font-display m-0 text-[22px] font-extrabold tracking-[-0.03em] text-foreground md:text-[26px]">
            {t("title")}
          </h1>
          <p className="mb-3 mt-1 text-[13.5px] leading-snug text-[var(--g-ink-2)]">
            {t("lede")}
          </p>
          <Field
            leading="search"
            placeholder={t("searchPh")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={t("searchPh")}
            data-testid="shops-search"
          />
          <div
            role="group"
            aria-label={t("filters.category")}
            className="mt-3 flex gap-2 overflow-x-auto pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {catLabels.map((label, i) => (
              <Pill
                key={SHOP_CATEGORIES[i] ?? i}
                active={categoryIndex === i}
                onClick={() => setCategoryIndex(i)}
              >
                {label}
              </Pill>
            ))}
          </div>
        </div>
      </header>

      {/* Body: mobile = map on top + list; desktop = 2-col (map | list).
          We branch on the JS `isDesktop` boolean (not CSS hide/show) so the
          map + list mount exactly once — a structural choice mirroring the
          prototype's `desktop ? … : …`. */}
      {isDesktop ? (
        <div className="grid flex-1 grid-cols-[1.1fr_0.9fr] overflow-hidden">
          <div className="border-r border-border p-6">{mapColumn("100%")}</div>
          <div className="overflow-y-auto p-6">{listColumn()}</div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto px-5 pb-24 pt-4">
          <div className="mb-4">{mapColumn(MOBILE_MAP_HEIGHT)}</div>
          {listColumn()}
        </div>
      )}

      {/* Seller detail sheet */}
      <BottomSheet
        open={openSeller !== null}
        onClose={closeSellerAndSync}
        desktop={isDesktop}
      >
        {openSeller !== null ? (
          <SellerDetail seller={openSeller} lang={lang} />
        ) : null}
      </BottomSheet>
    </div>
  );
}
